import i18n from "@/i18n";
import { detailOr } from "@/lib/apiErrorMessage";
/**
 * Customer Onboarding Wizard — 8-step guided setup (Prompt 22).
 *
 * Scope:
 *   - Lands corporate users on this screen when their company has
 *     `onboarding_status` ∈ {"pending", "in_progress"} (route guard lives
 *     in <OnboardingGate>).
 *   - 8 steps, each calling its corresponding existing endpoint to persist
 *     real data; `/api/onboarding/step` records *which* step finished so
 *     the wizard can resume after logout.
 *   - Required: company / restaurant / opening_inventory. The rest can be
 *     skipped.
 *
 * What this file is *not*:
 *   - A replacement for any operational page. Every endpoint called here
 *     is identical to what the regular pages use.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";
import {
    CheckCircle2, Circle, SkipForward, ArrowRight, Building2,
    Store, Boxes, Truck, Package, Users, ClipboardCheck, Sparkles,
    HelpCircle,
} from "lucide-react";

const STEPS = [
    { key: "company", get label() { return i18n.t("ui.Onboarding.STEPS.label_company", "Company"); }, icon: Building2, required: true,
      blurb: i18n.t("ui.onboarding.tellUsTheBasicsNameCountry", "Tell us the basics — name, country, time zone.") },
    { key: "restaurant", get label() { return i18n.t("ui.Onboarding.STEPS.label_restaurant", "Restaurant"); }, icon: Store, required: true,
      blurb: i18n.t("ui.onboarding.setUpTheFirstLocationThat", "Set up the first location that will use MiseOne.") },
    { key: "storage_areas", get label() { return i18n.t("ui.Onboarding.STEPS.label_storageAreas", "Storage areas"); }, icon: Boxes, required: false,
      blurb: i18n.t("ui.onboarding.whereYouKeepFoodDryStorage", "Where you keep food — dry storage, fridge, freezer.") },
    { key: "suppliers", get label() { return i18n.t("ui.Onboarding.STEPS.label_suppliers", "Suppliers"); }, icon: Truck, required: false,
      blurb: i18n.t("ui.onboarding.theVendorsThatDeliverToYour", "The vendors that deliver to your kitchen.") },
    { key: "products", get label() { return i18n.t("ui.Onboarding.STEPS.label_products", "Products"); }, icon: Package, required: false,
      blurb: i18n.t("ui.onboarding.optionalStartEmptyAndAddProducts", "Optional — start empty and add products as deliveries arrive.") },
    { key: "users", get label() { return i18n.t("ui.Onboarding.STEPS.label_users", "Users"); }, icon: Users, required: false,
      blurb: i18n.t("ui.onboarding.inviteYourHeadChefSousChefs_inviteYourHeadChefSousChefs", "Invite your head chef, sous chefs, and managers.") },
    { key: "opening_inventory", get label() { return i18n.t("ui.Onboarding.STEPS.label_openingInventory", "Opening inventory"); }, icon: ClipboardCheck, required: true,
      blurb: i18n.t("ui.onboarding.theStartingPointForEveryFuture", "The starting point for every future calculation.") },
    { key: "ready", get label() { return i18n.t("ui.Onboarding.STEPS.label_ready", "Ready"); }, icon: Sparkles, required: true,
      blurb: i18n.t("ui.onboarding.youAreSetTimeToCook", "You are set. Time to cook.") },
];

export default function Onboarding() {
    const { user } = useApp();
    const navigate = useNavigate();
    const [state, setState] = useState(null);
    const [stepIdx, setStepIdx] = useState(0);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!user) return;
        if (user.role !== "corporate") { navigate("/", { replace: true }); return; }
        api.get("/onboarding/state").then(({ data }) => {
            setState(data);
            // Resume at the next not-done step.
            const idx = STEPS.findIndex((s) => !data.onboarding_steps?.[s.key]?.done);
            setStepIdx(idx === -1 ? STEPS.length - 1 : idx);
        });
    }, [user, navigate]);

    const step = STEPS[stepIdx];
    const isDone = state?.onboarding_steps?.[step?.key]?.done;
    const progressPct = useMemo(() => {
        const done = STEPS.filter((s) => state?.onboarding_steps?.[s.key]?.done).length;
        return Math.round((done / STEPS.length) * 100);
    }, [state]);

    const persistStep = async (skipped = false) => {
        setBusy(true);
        try {
            const { data } = await api.post("/onboarding/step",
                { step: step.key, skipped });
            setState((s) => ({ ...s, ...data }));
            if (stepIdx < STEPS.length - 1) setStepIdx(stepIdx + 1);
        } catch (e) {
            toast.error(detailOr(e.response?.data?.detail, i18n.t("ui.onboarding.couldNotSaveStep", "Could not save step")));
        } finally { setBusy(false); }
    };

    const finish = async () => {
        setBusy(true);
        try {
            await api.post("/onboarding/complete");
            toast.success(i18n.t("ui.onboarding.welcomeToMiseone", "Welcome to MiseOne."));
            navigate("/workflow", { replace: true });
        } finally { setBusy(false); }
    };

    if (!user || !state) return null;
    if (state.onboarding_status === "completed") {
        return <Done finish={() => navigate("/workflow", { replace: true })} />;
    }

    return (
        <div className="min-h-screen bg-neutral-50" data-testid="onboarding-page">
            <header className="border-b border-neutral-200 bg-white">
                <div className="max-w-5xl mx-auto px-6 py-4 flex items-center gap-4">
                    <img src="/mise-one-logo.png" alt={i18n.t("ui.onboarding.miseone", "MiseOne")} className="h-10 w-10" />
                    <div>
                        <div className="font-brand text-lg font-semibold">{i18n.t("ui.onboarding.miseone", "MiseOne")}</div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                            {i18n.t("ui.onboarding.lessSetupMoreCooking", "Less setup. More cooking.")}
                        </div>
                    </div>
                    <div className="ml-auto text-sm text-neutral-500">
                        Step <span className="font-semibold text-neutral-900">{stepIdx + 1}</span>
                        {" "}of {STEPS.length}
                    </div>
                </div>
                <div className="h-1 bg-neutral-100">
                    <div className="h-full bg-neutral-900 transition-all" style={{ width: `${progressPct}%` }}
                         data-testid="onboarding-progress" />
                </div>
            </header>

            <main className="max-w-5xl mx-auto px-6 py-10 grid lg:grid-cols-[260px_1fr] gap-10">
                <aside data-testid="onboarding-checklist" className="space-y-2">
                    {STEPS.map((s, i) => {
                        const done = state.onboarding_steps?.[s.key]?.done;
                        const skipped = state.onboarding_steps?.[s.key]?.skipped;
                        const active = i === stepIdx;
                        return (
                            <button
                                key={s.key}
                                data-testid={`onboarding-step-link-${s.key}`}
                                onClick={() => setStepIdx(i)}
                                className={`w-full flex items-center gap-3 text-left px-3 py-2 rounded-md text-sm transition ${
                                    active ? "bg-neutral-900 text-white"
                                           : "hover:bg-white text-neutral-700"
                                }`}
                            >
                                {done
                                    ? <CheckCircle2 className={`h-4 w-4 ${active ? "text-emerald-300" : "text-emerald-600"}`} />
                                    : <Circle className="h-4 w-4 opacity-50" />}
                                <span className="flex-1">{s.label}</span>
                                {skipped && (
                                    <span className="text-[10px] uppercase tracking-[0.18em] opacity-70">
                                        skipped
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </aside>

                <section className="bg-white border border-neutral-200 rounded-lg p-8 lg:p-12 shadow-sm">
                    <div className="flex items-center gap-3 mb-2 text-xs uppercase tracking-[0.22em] text-neutral-500">
                        <step.icon className="h-4 w-4" />
                        Step {stepIdx + 1} · {step.label}
                        {!step.required && (
                            <span className="ml-2 px-1.5 py-0.5 rounded bg-neutral-100 text-neutral-600 normal-case tracking-normal">
                                optional
                            </span>
                        )}
                    </div>
                    <h2 className="font-display text-3xl font-extrabold tracking-tight">
                        {bodyTitle(step.key)}
                    </h2>
                    <p className="mt-2 text-neutral-500 max-w-prose">{step.blurb}</p>

                    <div className="mt-8">
                        <StepBody stepKey={step.key} isDone={isDone}
                                  onCompleted={() => persistStep(false)} />
                    </div>

                    <div className="mt-10 flex flex-wrap items-center justify-between gap-3 pt-6 border-t border-neutral-100">
                        <a
                            href="https://docs.miseone.app"
                            target="_blank" rel="noreferrer"
                            data-testid="onboarding-help-link"
                            className="inline-flex items-center gap-1.5 text-xs text-neutral-500 hover:text-neutral-900"
                        >
                            <HelpCircle className="h-3.5 w-3.5" /> Need help?
                        </a>
                        <div className="flex items-center gap-2">
                            {!step.required && !isDone && (
                                <button
                                    data-testid={`onboarding-skip-${step.key}`}
                                    onClick={() => persistStep(true)}
                                    disabled={busy}
                                    className="h-10 px-4 inline-flex items-center gap-1.5 rounded-md border border-neutral-300 text-sm text-neutral-700 hover:bg-neutral-50 disabled:opacity-50"
                                >
                                    <SkipForward className="h-3.5 w-3.5" /> {i18n.t("ui.onboarding.skipForNow", "Skip for now")}
                                </button>
                            )}
                            {step.key === "ready" ? (
                                <button
                                    data-testid="onboarding-finish"
                                    onClick={finish}
                                    disabled={busy}
                                    className="h-10 px-5 inline-flex items-center gap-1.5 rounded-md bg-neutral-900 text-white text-sm font-semibold hover:bg-neutral-800 disabled:opacity-50"
                                >
                                    Go to dashboard <ArrowRight className="h-3.5 w-3.5" />
                                </button>
                            ) : (
                                <button
                                    data-testid={`onboarding-mark-done-${step.key}`}
                                    onClick={() => persistStep(false)}
                                    disabled={busy}
                                    className="h-10 px-5 inline-flex items-center gap-1.5 rounded-md bg-neutral-900 text-white text-sm font-semibold hover:bg-neutral-800 disabled:opacity-50"
                                >
                                    {isDone ? i18n.t("ui.onboarding.nextStep", "Next step") : i18n.t("ui.onboarding.markAsDone", "Mark as done")} <ArrowRight className="h-3.5 w-3.5" />
                                </button>
                            )}
                        </div>
                    </div>
                </section>
            </main>
        </div>
    );
}

function bodyTitle(key) {
    return {
        company: i18n.t("ui.onboarding.welcomeToMiseone", "Welcome to MiseOne."),
        restaurant: i18n.t("ui.onboarding.createYourFirstRestaurant", "Create your first restaurant."),
        storage_areas: i18n.t("ui.onboarding.setUpYourStorageLocations", "Set up your storage locations."),
        suppliers: i18n.t("ui.onboarding.addTheVendorsYouBuyFrom", "Add the vendors you buy from."),
        products: i18n.t("ui.onboarding.howDoYouWantToStart", "How do you want to start your product list?"),
        users: i18n.t("ui.onboarding.inviteYourTeam", "Invite your team."),
        opening_inventory: i18n.t("ui.onboarding.runYourOpeningInventory", "Run your opening inventory."),
        ready: i18n.t("ui.onboarding.youAreReadyToCook", "You are ready to cook."),
    }[key] || key;
}

function StepBody({ stepKey, isDone, onCompleted }) {
    if (stepKey === "company")            return <CompanyForm onSaved={onCompleted} />;
    if (stepKey === "restaurant")         return <RestaurantForm onSaved={onCompleted} />;
    if (stepKey === "storage_areas")      return <SimpleChecklist note={i18n.t("ui.onboarding.recommendedDryStorageWalkInFridge", "Recommended: Dry storage · Walk-in fridge · Freezer · Bar · Cleaning supplies. Add them later from the Inventory page.")} done={isDone} />;
    if (stepKey === "suppliers")          return <SimpleChecklist note={i18n.t("ui.onboarding.addYourMainSuppliersLaterFrom", "Add your main suppliers later from the Suppliers page. Each delivery you record can also link to a supplier on the fly.")} done={isDone} />;
    if (stepKey === "products")           return <ProductsChoice onCompleted={onCompleted} />;
    if (stepKey === "users")              return <SimpleChecklist note={i18n.t("ui.onboarding.inviteYourHeadChefSousChefs_inviteYourHeadChefSousChefs", "Invite your head chef, sous chefs and managers from Settings → Users. You can also do this later.")} done={isDone} />;
    if (stepKey === "opening_inventory")  return <OpeningInventory />;
    if (stepKey === "ready")              return <Ready />;
    return null;
}

function CompanyForm({ onSaved }) {
    // Fully-managed SaaS: company details are provisioned by MiseOne
    // Super Admin. The customer only confirms and continues.
    const [company, setCompany] = useState(null);
    useEffect(() => {
        api.get("/companies/me").then(({ data }) => setCompany(data));
    }, []);
    if (!company) return <div className="text-sm text-neutral-500">{i18n.t("common.loading", "Loading…")}</div>;
    return (
        <div className="space-y-4 max-w-md" data-testid="onboarding-company-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                    [i18n.t("ui.onboarding.companyName", "Company name"), company.name],
                    [i18n.t("ui.onboarding.country", "Country"), company.country],
                    [i18n.t("ui.onboarding.timeZone", "Time zone"), company.default_timezone || company.timezone],
                    [i18n.t("ui.onboarding.currency", "Currency"), company.default_currency],
                ].map(([k, v]) => (
                    <div key={k}>
                        <div className="text-[10px] uppercase tracking-wide font-semibold text-neutral-500">{k}</div>
                        <div className="mt-1 font-medium text-neutral-900 text-sm">{v || "—"}</div>
                    </div>
                ))}
            </div>
            <p className="text-xs text-neutral-500">
                These details are managed by MiseOne. If anything needs to change,
                contact your account manager.
            </p>
            <button data-testid="onboarding-save-company"
                    onClick={onSaved}
                    className="h-10 px-4 rounded-md bg-neutral-900 text-white text-sm font-semibold">
                {i18n.t("ui.onboarding.looksGoodContinue", "Looks good — continue")}
            </button>
        </div>
    );
}

function RestaurantForm({ onSaved }) {
    // Fully-managed SaaS: restaurants are provisioned by MiseOne.
    // Onboarding shows the ones already created and lets the user
    // proceed.
    const [rests, setRests] = useState([]);
    useEffect(() => {
        api.get("/restaurants").then(({ data }) => setRests(data));
    }, []);
    return (
        <div className="space-y-4 max-w-lg" data-testid="onboarding-restaurant-form">
            {rests.length === 0 ? (
                <p className="text-sm text-neutral-600">
                    {i18n.t("ui.onboarding.yourRestaurantsWillAppearHereOnce", "Your restaurants will appear here once MiseOne finishes provisioning your account.")}
                </p>
            ) : (
                <ul className="space-y-2">
                    {rests.map((r) => (
                        <li key={r.id} className="rounded-md border border-neutral-200 bg-white p-3">
                            <div className="font-medium text-sm">{r.name}</div>
                            <div className="text-xs text-neutral-500">{r.city || "—"}</div>
                        </li>
                    ))}
                </ul>
            )}
            <p className="text-xs text-neutral-500">
                {i18n.t("ui.onboarding.toAddOrChangeARestaurant", "To add or change a restaurant, contact your MiseOne account manager.")}
            </p>
            <button data-testid="onboarding-save-restaurant"
                    onClick={onSaved}
                    disabled={rests.length === 0}
                    className="h-10 px-4 rounded-md bg-neutral-900 text-white text-sm font-semibold disabled:opacity-50">
                {i18n.t("ui.onboarding.continue", "Continue")}
            </button>
        </div>
    );
}

function ProductsChoice({ onCompleted }) {
    const navigate = useNavigate();
    const [busy, setBusy] = useState(false);
    const [selected, setSelected] = useState(null);

    const choose = async (choice) => {
        if (busy || choice === "import") return;   // Import is disabled
        setBusy(true);
        setSelected(choice);
        try {
            // Both "manual" and "empty" mark this optional step as complete.
            // "manual" additionally navigates to the Inventory page where
            // products can be added — the onboarding progress is already
            // persisted, so refreshing or coming back later resumes at the
            // next step.
            await onCompleted();
            if (choice === "manual") {
                // Give the toast a beat, then jump into Inventory.
                setTimeout(() => navigate("/inventory"), 150);
            }
        } finally {
            setBusy(false);
        }
    };
    const handleKey = (e, choice) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            choose(choice);
        }
    };

    const cards = [
        { k: "import", title: i18n.t("ui.onboarding.importExistingList", "Import existing list"),
          body: i18n.t("ui.onboarding.comingSoonBringYourSpreadsheetLater", "Coming soon — bring your spreadsheet later."), disabled: true },
        { k: "manual", title: i18n.t("ui.onboarding.createManually", "Create manually"),
          body: i18n.t("ui.onboarding.openTheProductsPageAndAdd", "Open the Products page and add what you need.") },
        { k: "empty",  title: i18n.t("ui.onboarding.startEmpty", "Start empty"),
          body: i18n.t("ui.onboarding.recommendedProductsAppearAutomaticallyWhenYou", "Recommended. Products appear automatically when you receive your first delivery.") },
    ];

    return (
        <div className="grid sm:grid-cols-3 gap-3" data-testid="onboarding-products-choice">
            {cards.map(({ k, title, body, disabled }) => {
                const isSelected = selected === k;
                return (
                    <button
                        key={k}
                        type="button"
                        role="button"
                        aria-disabled={disabled || busy}
                        disabled={disabled || busy}
                        data-testid={`onboarding-products-${k}`}
                        onClick={() => choose(k)}
                        onKeyDown={(e) => !disabled && handleKey(e, k)}
                        className={
                            "text-left p-4 rounded-lg border transition " +
                            (disabled
                                ? "border-neutral-100 bg-neutral-50 opacity-60 cursor-not-allowed"
                                : "border-neutral-200 bg-white hover:border-neutral-400 hover:bg-neutral-50 cursor-pointer focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:ring-offset-2") +
                            (isSelected ? " border-neutral-900 shadow-sm" : "")
                        }
                    >
                        <div className="text-sm font-semibold text-neutral-900">{title}</div>
                        <div className="mt-1 text-xs text-neutral-500">{body}</div>
                        {isSelected && busy && (
                            <div className="mt-2 text-[11px] font-semibold text-neutral-500">
                                {i18n.t("common.saving", "Saving…")}
                            </div>
                        )}
                    </button>
                );
            })}
        </div>
    );
}

function OpeningInventory() {
    return (
        <div className="space-y-4 max-w-md" data-testid="onboarding-opening-inventory">
            <p className="text-sm text-neutral-700 leading-relaxed">
                Before MiseOne can track waste, food cost or inventory variance,
                you need a starting point. Run a physical count of everything you
                currently have in stock.
            </p>
            <p className="text-sm text-neutral-500 leading-relaxed">
                {i18n.t("ui.onboarding.useTheInventoryPageYouCan", "Use the Inventory page — you can come back here when you are done. We will mark this step complete automatically when your first count is submitted.")}
            </p>
            <a href="/inventory"
               data-testid="onboarding-start-inventory"
               className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-neutral-900 text-white text-sm font-semibold">
                Open Inventory <ArrowRight className="h-3.5 w-3.5" />
            </a>
        </div>
    );
}

function Ready() {
    return (
        <div className="space-y-4" data-testid="onboarding-ready">
            <p className="text-lg text-neutral-700">{i18n.t("ui.onboarding.congratulationsYourRestaurantIsReady", "Congratulations — your restaurant is ready.")}</p>
            <div className="grid sm:grid-cols-2 gap-3 max-w-2xl">
                {[
                    { title: i18n.t("ui.onboarding.createRecipes", "Create recipes"), path: "/recipes" },
                    { title: i18n.t("ui.onboarding.receiveFirstDelivery", "Receive first delivery"), path: "/deliveries" },
                    { title: i18n.t("ui.onboarding.createWeeklyMenu", "Create weekly menu"), path: "/weekly-menu" },
                    { title: i18n.t("ui.onboarding.startDailyWorkflow", "Start daily workflow"), path: "/workflow" },
                ].map((c) => (
                    <a key={c.path} href={c.path}
                       className="p-4 rounded-lg border border-neutral-200 bg-white hover:bg-neutral-50">
                        <div className="text-sm font-semibold">{c.title}</div>
                    </a>
                ))}
            </div>
        </div>
    );
}

function SimpleChecklist({ note, done }) {
    return (
        <div className="text-sm text-neutral-700 leading-relaxed bg-neutral-50 p-4 rounded-md border border-neutral-200">
            {note}
            {done && <div className="mt-2 text-xs text-emerald-700">{i18n.t("ui.onboarding.markedAsDoneYouCanMove", "Marked as done — you can move on.")}</div>}
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="block text-[11px] font-semibold uppercase tracking-[0.18em] text-neutral-700 mb-1.5">
                {label}
            </span>
            {children}
        </label>
    );
}

function Done({ finish }) {
    return (
        <div className="min-h-screen flex items-center justify-center">
            <button onClick={finish}
                    className="px-4 h-10 rounded-md bg-neutral-900 text-white font-semibold">
                {i18n.t("ui.onboarding.goToDashboard", "Go to dashboard")}
            </button>
        </div>
    );
}
